import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import BasicTable, { NUMERIC_CELL_SX } from '../../components/BasicTable/BasicTable';
import { backState } from '../../components/BackLink';
import { ITableHeadings } from '../../common/interfaces/common.interface';
import { formatMonthKey, formatSerbianDate } from '../../utilities/helper/date.helper';
import { FINANCE } from '../../common/constants/routes.constants';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import GenericButton, { GenericButtonTypeEnum } from '../../components/GenericButton';
import GenericInfoModal from '../../components/GenericInfoModal';
import { MESSAGE_TYPE } from '../../common/constants/common.constants';
import { FINANCE_INVOICES } from '../../common/constants/routes.constants';
import BackLink from '../../components/BackLink';
import { createNotification } from '../../utilities/helper/notification.helper';
import { formatMoney } from '../../utilities/helper/money.helper';
import { BATCH_OUTCOME_LABELS, InvoiceBatchOutcomeEnum, InvoiceBatchStatusEnum } from '../../common/enums/invoicing.enum';
import { InvoiceBatchDto, InvoiceBatchEntryDto } from '../../common/interfaces/invoicing.interface';
import { discardInvoiceBatch, getInvoiceBatch, issueInvoiceBatch } from '../../api/invoicing.api';
import { useTranslation } from 'react-i18next';

const BATCH_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'common.child', sortable: false, width: '20%' },
  { id: 2, value: '', label: 'common.group', sortable: false, width: '14%' },
  { id: 3, value: '', label: 'invoices.runs.outcome', sortable: false, width: '12%' },
  { id: 4, value: '', label: 'invoices.runs.invoice', sortable: false, width: '14%' },
  { id: 5, value: '', label: 'invoices.columns.total', sortable: false, width: '12%', align: 'right' },
  { id: 6, value: '', label: 'common.note', sortable: false }
];

const OUTCOME_COLOR: Record<InvoiceBatchOutcomeEnum, 'default' | 'success' | 'warning' | 'error'> = {
  [InvoiceBatchOutcomeEnum.CREATED]: 'default',
  [InvoiceBatchOutcomeEnum.ISSUED]: 'success',
  [InvoiceBatchOutcomeEnum.SKIPPED]: 'warning',
  [InvoiceBatchOutcomeEnum.FAILED]: 'error'
};

/**
 * What a generation run produced, and what it could not.
 *
 * Issuing is partially successful by nature - a payer goes missing, a contract number
 * was never set - so the result is a table rather than a toast. A row that failed says
 * why, against the child it belongs to, which is what the admin needs in order to fix
 * it and run again.
 */
const InvoiceBatchPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { batchId } = useParams<{ batchId: string }>();
  const kindergartenId = new URLSearchParams(location.search).get('kindergartenId') ?? '';

  const [batch, setBatch] = useState<InvoiceBatchDto | null>(null);
  const [loader, setLoader] = useState(true);
  const [confirmIssue, setConfirmIssue] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const listHref = `${FINANCE_INVOICES}?kindergartenId=${kindergartenId}${batch ? `&month=${batch.periodStart.slice(0, 7)}` : ''}`;
  const openInvoice = (entry: InvoiceBatchEntryDto) => {
    if (!entry.invoiceId) return;
    navigate(
      `${FINANCE}/invoice/${entry.invoiceId}?kindergartenId=${kindergartenId}`,
      backState(`${location.pathname}${location.search}`, t('invoices.runs.run'))
    );
  };

  const load = useCallback(async () => {
    if (!kindergartenId || !batchId) return;
    setLoader(true);
    try {
      const result = await getInvoiceBatch(kindergartenId, batchId);
      setBatch(result.results ?? null);
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('invoices.runs.runLoadFailed'));
    } finally {
      setLoader(false);
    }
  }, [kindergartenId, batchId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleIssue = async () => {
    if (!kindergartenId || !batchId) return;
    try {
      const result = await issueInvoiceBatch(kindergartenId, batchId);
      setBatch(result.results ?? null);
      setConfirmIssue(false);
      const issued = result.results?.issuedCount ?? 0;
      const failed = result.results?.failedCount ?? 0;
      createNotification(
        failed ? MESSAGE_TYPE.ERROR : MESSAGE_TYPE.SUCCESS,
        failed ? t('invoices.runs.issuePartial', { issued, failed }) : t('invoices.bulk.issued', { count: issued })
      );
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('invoices.runs.issueFailed'));
    }
  };

  const handleDiscard = async () => {
    if (!kindergartenId || !batchId) return;
    try {
      await discardInvoiceBatch(kindergartenId, batchId);
      createNotification(MESSAGE_TYPE.SUCCESS, t('invoices.runs.discarded'));
      navigate(listHref);
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('invoices.runs.discardFailed'));
    }
  };

  if (loader) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!batch) {
    return <Typography sx={{ p: 3 }}>{t('invoices.runs.gone')}</Typography>;
  }

  const runMonth = batch.periodStart.slice(0, 7);
  const canIssue = batch.status === InvoiceBatchStatusEnum.DRAFTS_READY && batch.createdCount > 0;
  const needsAttention = batch.entries.filter(
    entry => entry.outcome === InvoiceBatchOutcomeEnum.SKIPPED || entry.outcome === InvoiceBatchOutcomeEnum.FAILED
  );

  const renderRow = (entry: InvoiceBatchEntryDto) => (
    // a row with an invoice leads to it; the rest have nothing to open
    <TableRow
      key={entry.childId}
      hover={!!entry.invoiceId}
      tabIndex={entry.invoiceId ? 0 : undefined}
      sx={entry.invoiceId ? { cursor: 'pointer' } : undefined}
      onClick={() => openInvoice(entry)}
      onKeyDown={event => {
        if (event.key === 'Enter') openInvoice(entry);
      }}
    >
      <TableCell>{entry.childFullName}</TableCell>
      <TableCell>{entry.groupName ?? '—'}</TableCell>
      <TableCell>
        <Chip size="small" variant="outlined" color={OUTCOME_COLOR[entry.outcome]} label={t(BATCH_OUTCOME_LABELS[entry.outcome])} />
      </TableCell>
      <TableCell>{entry.invoiceNumber ?? '—'}</TableCell>
      <TableCell sx={NUMERIC_CELL_SX}>{entry.totalGross === null ? '—' : formatMoney(entry.totalGross)}</TableCell>
      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
        {entry.reason ??
          (entry.attendanceRecount
            ? t('invoices.attendanceRecounted', { from: entry.attendanceRecount.from, to: entry.attendanceRecount.to })
            : entry.warnings.length
              ? entry.warnings.join(' ')
              : '—')}
        {/* a warning on an otherwise fine row still deserves to be seen: mid-period
            enrolments are billed in full and nobody notices until a parent does */}
        {!entry.reason && entry.warnings.length > 0 && (
          <Tooltip title={entry.warnings.join(' ')}>
            <Chip size="small" color="warning" variant="outlined" label={t('invoices.runs.check')} sx={{ ml: 1 }} />
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 3 }}>
      <BackLink fallbackTo={`${FINANCE_INVOICES}?kindergartenId=${kindergartenId}&month=${runMonth}`} fallbackLabel={t('invoices.title')} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          {/* the run is for a month, so it is named as one, not as two ISO dates */}
          <Typography variant="h2">{formatMonthKey(runMonth)}</Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }}>
            {t('invoices.runs.generatedOn', { date: formatSerbianDate(batch.createdAt) })}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip size="small" label={t('invoices.runs.draftsCount', { count: batch.createdCount })} />
            <Chip size="small" color="success" label={t('invoices.runs.issuedCount', { count: batch.issuedCount })} />
            <Chip size="small" color="warning" label={t('invoices.runs.skippedCount', { count: batch.skippedCount })} />
            <Chip size="small" color="error" label={t('invoices.runs.failedCount', { count: batch.failedCount })} />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <GenericButton
            buttonType={GenericButtonTypeEnum.Neutral}
            variant="outlined"
            onClick={() => setConfirmDiscard(true)}
            label={t('invoices.runs.discard')}
          />
          {canIssue && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              onClick={() => setConfirmIssue(true)}
              label={t('invoices.runs.issueInvoices', { count: batch.createdCount })}
            />
          )}
        </Stack>
      </Stack>

      {!!needsAttention.length && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {t('invoices.runs.notInvoiced', { count: needsAttention.length })}
        </Alert>
      )}

      <Box sx={{ border: 1, borderColor: 'line', borderRadius: 4 }}>
        <BasicTable
          headings={BATCH_HEADINGS}
          loader={false}
          // one run is a few dozen children at most: shown whole, not paged
          metadata={{ currentPage: 1, itemsPerPage: Math.max(batch.entries.length, 25), totalItems: batch.entries.length, totalPages: 1 }}
          setMetadata={() => undefined}
        >
          {batch.entries.map(renderRow)}
        </BasicTable>
      </Box>

      <GenericInfoModal
        open={confirmIssue}
        onClose={() => setConfirmIssue(false)}
        onConfirm={handleIssue}
        label={t('invoices.runs.issueAllTitle')}
        message={t('invoices.runs.issueAllConfirm')}
        confirmLabel={t('invoices.runs.issue')}
        entityName={t('invoices.runs.invoicesCount', { count: batch.createdCount })}
      />
      <GenericInfoModal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={handleDiscard}
        label={t('invoices.runs.discardTitle')}
        message={t('invoices.runs.discardConfirm')}
        confirmLabel={t('invoices.runs.discardShort')}
        entityName={formatMonthKey(runMonth)}
      />
    </Box>
  );
};

export default InvoiceBatchPage;
